import { FC, useEffect } from 'react';
import { ReactComponent as Plus } from 'assets/icons/purchase-page-icons/plus-white.svg';
import { ReactComponent as WarningIcon } from 'assets/icons/purchase-page-icons/warning-big.svg';
import dayjs from 'dayjs';
import { useUptime } from 'hooks/useUptime';
import { TAssetsData } from 'store/api/cuverse-api/assets/types/assets-response.interface';
import { useAppDispatch } from 'store/store';
import { requestUptimeThunkAction } from 'store/uptime-reducer/uptime.thunk-action';

import { useLanguage } from 'components/logic/LanguageProvider';
import { LANGUAGES } from 'components/logic/LanguageProvider/types/language-provider.interface';
import { dayDeclension } from 'utils/declensions/day-declension';
import { formatUsdOutput } from 'utils/formatters/format-usd-output.util';

import * as S from './InvoiceStatus.styled';

interface IInvoiceStatusProps {
  minersPrices: { id: number; price: number }[];
  btcUsdRate: number;
  assetsData: TAssetsData;
  launchDate?: string;
  paymentBalanceUsd: number;
  availableDays: number;
  electricityFee: number;
  isOnlyTrialMinerBought: boolean;
  handleReplenishClick: () => void;
  t: (key: string) => string;
}

export const InvoiceStatus: FC<IInvoiceStatusProps> = ({
  assetsData,
  launchDate,
  paymentBalanceUsd,
  availableDays,
  isOnlyTrialMinerBought,
  handleReplenishClick,
  t,
}) => {
  const dispatch = useAppDispatch();
  const { language } = useLanguage();

  const { lastUptimeToggle } = useUptime();

  useEffect(() => {
    if (!lastUptimeToggle) {
      void dispatch(requestUptimeThunkAction());
    }
  }, [dispatch, lastUptimeToggle]);

  const dailyTotalElectricityFees = assetsData.map((miner) => miner.dailyTotalFee);

  const totalDailyElectricityFee = dailyTotalElectricityFees.reduce((a, b) => {
    if (!b) return a;
    return a + b;
  }, 0);

  if (!launchDate?.includes('within 24 hours') && launchDate?.includes('/')) {
    const [day, month, year] = launchDate?.split('/') ?? ['', '', ''];
    launchDate = dayjs(`${year}-${month}-${day}`).format('DD.MM.YYYY');
  } else {
    launchDate = t('purchase.within24hours');
  }

  return (
    <S.InvoiceStatus>
      <S.TittleBlock>
        <S.Title>{t('purchase.invoicePaidTitle')}</S.Title>
        <S.LaunchNotion>
          {t('purchase.minerDateStartText')} <S.LaunchDate>{launchDate}</S.LaunchDate>
        </S.LaunchNotion>
      </S.TittleBlock>
      {!isOnlyTrialMinerBought && (
        <>
          <S.WarningBlock>
            <WarningIcon />
            <S.WarningText>
              {t('purchase.electricityFeeNotificationText')}{' '}
              <S.WarningSpan>(${formatUsdOutput(totalDailyElectricityFee)})</S.WarningSpan>
            </S.WarningText>
          </S.WarningBlock>
          <S.ElectricityBalanceBlock as="dl">
            <S.BalanceTitle as="dt">{t('purchase.electricityBalanceTitle')}</S.BalanceTitle>
            <S.BalanceValue as="dd">
              <S.BalanceUsd>{`$${formatUsdOutput(paymentBalanceUsd)}`}</S.BalanceUsd>
              <S.BalanceDays>
                {availableDays}{' '}
                {language === LANGUAGES.RUS ? dayDeclension(availableDays, t) : t('purchase.days')}
              </S.BalanceDays>
            </S.BalanceValue>
            <S.ReplenishButton
              text={t('deposit.deposit')}
              icon={<Plus />}
              onClick={handleReplenishClick}
            />
          </S.ElectricityBalanceBlock>
        </>
      )}
    </S.InvoiceStatus>
  );
};
